<script setup>
import { computed } from 'vue';
import { formatDuration } from 'shared/helpers/timeHelper';
import Avatar from 'next/avatar/Avatar.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Spinner from 'shared/components/Spinner.vue';
import { useVoiceCallMetrics } from '../../composables/useVoiceCallMetrics';

const { metrics, isLoading } = useVoiceCallMetrics('agents', {
  includePresence: true,
});

const statusClasses = {
  in_call: 'bg-n-blue-9',
  available: 'bg-n-teal-9',
  busy: 'bg-n-amber-9',
};

const callDuration = startedAt => {
  if (!startedAt) return '---';
  return formatDuration(Math.max(0, Math.floor(Date.now() / 1000) - startedAt));
};

const rows = computed(() => metrics.value.agents || []);
</script>

<template>
  <div class="w-full overflow-x-auto">
    <table v-if="rows.length" class="w-full min-w-[760px] text-sm">
      <thead>
        <tr class="border-b border-n-weak text-start text-xs text-n-slate-11">
          <th class="px-3 py-3 text-start font-medium">
            {{ $t('VOICE_CALL_DASHBOARD.AGENTS.STATUS') }}
          </th>
          <th class="px-3 py-3 text-start font-medium">
            {{ $t('VOICE_CALL_DASHBOARD.AGENTS.AGENT') }}
          </th>
          <th class="px-3 py-3 text-start font-medium">
            {{ $t('VOICE_CALL_DASHBOARD.AGENTS.CONTACT') }}
          </th>
          <th class="px-3 py-3 text-start font-medium">
            {{ $t('VOICE_CALL_DASHBOARD.AGENTS.DIRECTION') }}
          </th>
          <th class="px-3 py-3 text-start font-medium">
            {{ $t('VOICE_CALL_DASHBOARD.AGENTS.DURATION') }}
          </th>
          <th class="px-3 py-3 text-start font-medium">
            {{ $t('VOICE_CALL_DASHBOARD.AGENTS.INBOX') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="agent in rows"
          :key="agent.id"
          class="border-b border-n-weak text-n-slate-12 last:border-b-0"
        >
          <td class="px-3 py-3">
            <span class="flex items-center gap-2 whitespace-nowrap">
              <span
                class="size-2 rounded-full"
                :class="statusClasses[agent.status]"
              />
              {{
                $t(
                  `VOICE_CALL_DASHBOARD.AGENT_STATUS.${agent.status.toUpperCase()}`
                )
              }}
            </span>
          </td>
          <td class="px-3 py-3">
            <div class="flex items-center gap-2">
              <Avatar
                :src="agent.thumbnail"
                :name="agent.name"
                :size="32"
                hide-offline-status
                rounded-full
              />
              <div class="min-w-0">
                <div class="truncate">{{ agent.name }}</div>
                <div class="truncate text-xs text-n-slate-11">
                  {{ agent.email }}
                </div>
              </div>
            </div>
          </td>
          <td class="px-3 py-3">
            <template v-if="agent.call">
              <router-link
                :to="{
                  name: 'inbox_conversation',
                  params: { conversation_id: agent.call.conversation_id },
                }"
                class="hover:underline"
              >
                {{ agent.call.contact_name }}
              </router-link>
              <div class="text-xs text-n-slate-11">
                {{ agent.call.phone_number || '---' }}
              </div>
            </template>
            <span v-else class="text-n-slate-11">---</span>
          </td>
          <td class="px-3 py-3">
            <span v-if="agent.call">
              {{
                $t(
                  `VOICE_CALL_DASHBOARD.DIRECTION.${agent.call.direction.toUpperCase()}`
                )
              }}
            </span>
            <span v-else class="text-n-slate-11">---</span>
          </td>
          <td class="px-3 py-3">
            {{ agent.call ? callDuration(agent.call.started_at) : '---' }}
          </td>
          <td class="px-3 py-3">
            {{ agent.call?.inbox_name || '---' }}
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else-if="isLoading" class="flex items-center justify-center p-8">
      <Spinner />
      <span>{{ $t('VOICE_CALL_DASHBOARD.LOADING') }}</span>
    </div>
    <EmptyState v-else :title="$t('VOICE_CALL_DASHBOARD.AGENTS.NO_AGENTS')" />
  </div>
</template>
